import superjson from 'superjson';
import Cookies from 'js-cookie';
import type { AppRouter } from '@adflow/api';
import { CreateTRPCProxyClient, createTRPCProxyClient, httpBatchLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import { APIClientContext as FigmaAPIClientContext } from '@adflow/figma';
import { APIClientContext as ShadowBrandAPIClientContext } from '@adflow/shadow-brand';
import { APIClientContext as MarketingCloudAPIClientContext } from '@adflow/marketing-cloud';
import { APIClientContext as CreatomateAPIClientContext } from '@adflow/creatomate';
import { QueryClient } from '@tanstack/react-query';

import templateEnv from './env';
import { useState } from 'react';
import {
  generateUserToCheck,
  userToRequestContextAuthorizationHeader,
} from '../../utils/user-context';

export const trpc = createTRPCReact<AppRouter>();

export function useTRPCReact(_requestContextAuthorizationHeader?: string) {
  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState<ReturnType<typeof trpc.createClient>>(() =>
    trpc.createClient({
      transformer: superjson,
      links: [
        httpBatchLink({
          url: `${templateEnv.apiURL}/trpc`,
          fetch(url, options) {
            return fetch(url, {
              ...options,
              credentials: 'include',
            });
          },
          headers: () => {
            const headers: { [key: string]: string } = {
              Authorization: getAuthorizationHeader(),
            };
            if (_requestContextAuthorizationHeader) {
              headers['X-Event-Request-Context-Authorizer'] = _requestContextAuthorizationHeader;
            }
            return headers;
          },
        }),
      ],
    }),
  );

  return { trpcClient, queryClient };
}

export class TRPCClient {
  private static _requestContextAuthorizationHeader = '';
  static setRequestContextAuthorizationHeader(header: string) {
    this._requestContextAuthorizationHeader = header;
  }

  static client: CreateTRPCProxyClient<AppRouter> = createTRPCProxyClient<AppRouter>({
    transformer: superjson,
    links: [
      httpBatchLink({
        url: `${templateEnv.apiURL}/trpc`,
        fetch(url, options) {
          return fetch(url, {
            ...options,
            credentials: 'include',
          });
        },
        headers: () => {
          const headers: { [key: string]: string } = {
            Authorization: getAuthorizationHeader(),
          };

          // Try to get request context header (priority: static header > cookie fallback)
          // Works in all environments: local mode uses static header from App.tsx,
          // staging/prod uses cookie fallback for screenshot service
          let requestContextHeader = this._requestContextAuthorizationHeader;

          // If no static header, try cookie fallback (works in all environments)
          if (!requestContextHeader) {
            const userFromCookie = generateUserToCheck(null);
            if (userFromCookie) {
              requestContextHeader = userToRequestContextAuthorizationHeader(userFromCookie);
            }
          }

          // Set header if we have one
          if (requestContextHeader) {
            headers['X-Event-Request-Context-Authorizer'] = requestContextHeader;
          }

          return headers;
        },
      }),
    ],
  });
  static figmaClient: FigmaAPIClientContext.APIClient = {
    importFigmaComponent: this.client.figma.importFigmaComponent.mutate,
    getComponentImage: this.client.figma.getComponentImages.query,
    getComponents: this.client.figma.listComponentsForFile.query,
    getExportableComponentAdElements: this.client.figma.listComponentNodes.query,
    listSavedComponents: this.client.figma.figmaComponentsList.query,
    upsertFigmaComponent: this.client.figma.upsertFigmaComponent.mutate,
  };

  static creatomateClient: CreatomateAPIClientContext.APIClient = {
    importCreatomateTemplate: this.client.video.v1.importCreatomateTemplate.query,
    createThumbnail: this.client.video.v1.createThumbnail.query,
  };

  static consoleClient: ShadowBrandAPIClientContext.APIClient = {
    listTeams: this.client.consoleAPIGraph.listTeams.query,
    listTeamBrands: this.client.consoleAPIGraph.listTeamBrands.query,
    getBrand: this.client.consoleAPIGraph.getBrand.query,
    listTeamFonts: this.client.consoleAPIGraph.listFonts.query,
  };

  static marketingCloudClient: MarketingCloudAPIClientContext.APIClient = {
    listAdvertisers: async () => {
      const result = await this.client.marketingCloud.listAdvertisers.query();

      if (Array.isArray(result)) {
        return result;
      }

      return result.advertisers.map((r) => ({
        _id: r.id,
        name: r.name,
        type: r.type,
      }));
    },
    // Brand methods are implemented directly in SelectAdvertiser using Apollo client
    // These stubs are provided to satisfy the type interface
    getBrandsByAdvertiser: async () => {
      throw new Error('getBrandsByAdvertiser should be called via Apollo client directly');
    },
    getBrandById: async () => {
      throw new Error('getBrandById should be called via Apollo client directly');
    },
  };
}
function getAuthorizationHeader(): string {
  const cookie = Cookies.get('token');
  if (!cookie) {
    console.warn("Cookie 'token' not found");
    return '';
  }
  return `bearer ${cookie} paid-social:test:read`;
}
